import type { HubBuyingOffer } from 'components/hub/useHubPurchaseOffers';
import {
  buildPropertyPhotoUrl,
  buildPropertySpecsLine,
} from 'components/marketplace/property/helpers';
import { getTaskGroupDisabledState, isInContract, isOfferCanceled } from 'helpers/buyerOfferStatus';
import { formatCurrency } from 'helpers/formatters';

import type { TransactionCardModel } from './TransactionCard';

export interface OfferCardModel {
  id: string;
  href: string;
  photoUrl: string | null;
  addressLine: string;
  priceLabel: string | null;
  detailsLine: string | null;
  isCanceled: boolean;
  /** Segments for the progress bar; total is the task count across all groups. */
  totalSegments: number;
  completedSegments: number;
  /** Title of the next incomplete task, e.g. "Request and sign contract". */
  primaryTaskLabel: string | null;
  /** Count of not-yet-complete tasks across all groups. */
  newTaskCount: number;
  transactionState: HubBuyingOffer['transactionState'];
}

export const buildOfferCardModel = (offer: HubBuyingOffer): OfferCardModel => {
  const listing = offer.listing?.marketplaceListing;
  const isCanceled = isOfferCanceled(offer.transactionState);

  // For active offers, only count tasks in groups that are actually available
  // for the current transaction state — the API can return future phases (escrow
  // & closing, welcome home) that the buyer can't act on yet, and advertising
  // those as "new tasks" would be misleading. Cancelled offers keep every group
  // so the card can still render its (greyed, fully-complete) progress bar.
  const groups = isCanceled
    ? (offer.taskGroups ?? [])
    : (offer.taskGroups ?? []).filter(
        (group) => !getTaskGroupDisabledState(group.phase, offer.transactionState).disabled,
      );

  // Progress is task-level (summed across groups) so the bar fills as individual
  // tasks complete, rather than only when an entire phase finishes.
  const totalSegments = groups.reduce((sum, group) => sum + (group.progress?.total ?? 0), 0);
  const completedSegments = groups.reduce(
    (sum, group) => sum + (group.progress?.completed ?? 0),
    0,
  );
  const newTaskCount = Math.max(0, totalSegments - completedSegments);

  // Surface the next actionable task (e.g. "Request and sign contract"), not the
  // phase/group it belongs to. Tasks are returned in journey order, so the first
  // PENDING one across all groups is the buyer's next step. A null/absent state
  // (e.g. informational tasks) is not actionable and is skipped.
  const primaryTaskLabel =
    groups.flatMap((group) => group.tasks ?? []).find((task) => task.state === 'PENDING')?.title ??
    null;

  const addressLine = [
    listing?.street,
    [listing?.city, listing?.state].filter(Boolean).join(', '),
    listing?.postalCode,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    id: offer.id,
    href: `/dashboard/buy/${offer.id}`,
    photoUrl: buildPropertyPhotoUrl(listing?.photoUrls[0], { preset: '3:2-sm' }),
    addressLine: addressLine || 'Your offer',
    priceLabel: offer.priceCents === null ? null : formatCurrency(offer.priceCents),
    detailsLine: buildPropertySpecsLine(
      listing?.bedrooms,
      listing?.bathrooms,
      listing?.totalLivingSquareFootage,
    ),
    isCanceled,
    totalSegments,
    completedSegments,
    primaryTaskLabel,
    newTaskCount,
    transactionState: offer.transactionState,
  };
};

const buildFooterMessage = (offer: OfferCardModel): string => {
  if (offer.isCanceled) {
    return 'Offer is no longer active';
  }
  if (offer.newTaskCount > 0) {
    return 'Tasks pending';
  }
  return 'You have 0 new tasks';
};

const buildStatusLabel = (offer: OfferCardModel): string => {
  if (offer.isCanceled) {
    return 'Offer withdrawn';
  }
  if (isInContract(offer.transactionState)) {
    return 'Under contract';
  }
  if (offer.primaryTaskLabel === 'Contract') {
    return 'Under contract';
  }
  return offer.primaryTaskLabel ?? 'View progress';
};

export const buildOfferTransactionCardModel = (offer: OfferCardModel): TransactionCardModel => {
  // A canceled offer shows a fully-filled (but muted) progress bar rather than
  // its partial in-flight progress.
  const completedSegments = offer.isCanceled ? offer.totalSegments : offer.completedSegments;

  return {
    id: offer.id,
    href: offer.href,
    media: { kind: 'image', url: offer.photoUrl },
    addressLine: offer.addressLine,
    priceLabel: offer.priceLabel,
    detailsLine: offer.detailsLine,
    isMuted: offer.isCanceled,
    isComplete: false,
    totalSegments: offer.totalSegments,
    completedSegments,
    statusLabel: buildStatusLabel(offer),
    footerMessage: buildFooterMessage(offer),
    ctaLabel: offer.isCanceled ? 'View details' : 'View task',
    ctaAnalyticsName: offer.isCanceled ? 'hub-offer-view-details' : 'hub-offer-view-task',
    ctaVariant: offer.isCanceled ? 'secondary' : 'primary',
  };
};
